"use strict";

import toFinite from "lodash/toFinite";

const DEFAULT_API_ARGS = {
  size: 50,
  from: 0
};

/**
 * @todo Remove generator stuff. Just use promises.
 */
const MentorTimeTrackerApi = function(apiURL, auth0) {
  const me = { auth0 };

  me.endPoints = {
    accountOperations: {
      accounts: "/accounts",
    },
    adminOperations: {
      categories: "/categories",
      singleCategory: "/categories/{id}",
    },
    productOperations: {
      productGroups: "/products/groups",
    },
    distributorOperations: {
      distributors: "/distributors",
    },
    opportunityOperations: {
      opportunities: "/opportunities",
    },
    interlockOperations: {
      interlocks: "/interlocks",
    },
    userRoleOperations: {
      roles: "/roles",
      rolesOps: "/roles/{id}",
    },
    timeEntryOperations: {
      initialDate: "/timeentries/initialdate",
      totalHours: "/timeentries/totalhours",
      timeEntries: "/timeentries",
      timeEntriesBatch: "/timeentries/_batch",
      timeEntriesOps: "/timeentries/{id}",
      timeEntriesSubmit: "/timeentries/{id}/submit",
    },
    userOperations: {
      user: "/user",
      userFavorites: "/user/favorites",
      userPreferences: "/user/preferences",
    },
    errorReportOperations: {
      reportError: "/report-error"
    },
    serviceRequestOperations: {
      serviceRequests: "/servicerequests",
      singleServiceRequest: "/servicerequests/{id}"
    }
  };

  const _encodeQuery = function (hash) {
    return Object.keys(hash).map((i) => `${i}=${hash[i]}`).join("&");
  };

  async function _fetchDefaultOptions(method) {
    const result = {
      method: method,
      headers: {
        "Content-Type": "application/json",
        "X-Api-Version-Preference": "1.0.2"
      }
    };

    if (auth0) {
      const { token: accessToken } = await auth0.getAccessTokenSilently();

      const { __raw: token } = await auth0.getIdTokenClaims();

      result.headers.Authorization = `Bearer ${token}`;
    } else {
      throw new Error("Auth0 Missing");
    }

    return result;
  };

  const _asyncToGenerator = function (fn) {
    return function () {
      var gen = fn.apply(this, arguments);
      return new Promise(function(resolve, reject) {
        var callNext = step.bind(null, "next");
        var callThrow = step.bind(null, "throw");

        function step(key, arg) {
          try {
            var info = gen[key](arg);
            var value = info.value;
          } catch (error) {
            reject(error);
            return;
          }

          if (info.done) {
            resolve(value);
          } else {
            Promise.resolve(value).then(callNext, callThrow);
          }
        }
        callNext();
      });
    };
  };

  async function getRequestActual(endPoint, extraData = {}) {
    const url = `${apiURL}${endPoint}?${_encodeQuery(extraData)}`;
    let response = null;

    try {
      const options = await _fetchDefaultOptions("GET");

      response = await fetch(url, options);

      if (response.status === 404) {
        return null;
      }

      return await response.json();

    } catch (error) {
      console.warn("Error", error);
    }

    return response;
  }

  const getRequest = _asyncToGenerator(function*(...args) {
    const resp = yield getRequestActual(...args);

    return resp;
  });

  async function requestActual(method, endPoint, data, dataInUrl = false) {
    let url = `${apiURL}${endPoint}`;
    let sendData = await _fetchDefaultOptions(method);

    if (dataInUrl) {
      url += `?${_encodeQuery(data)}`;
    } else {
      sendData.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, sendData);

      if (response.status === 204) {
        return response;
      } else {
        return await response.json();
      }
    } catch (error) {
      console.error("Error", error);

      return null;
    }
  }

  const request = _asyncToGenerator(function* (...args) {
    return yield requestActual(...args);
  });

  const postRequest = _asyncToGenerator(function* (endPoint, data, dataInUrl = false) {
    return yield request("POST", endPoint, data, dataInUrl);
  });

  const putRequest = _asyncToGenerator(function* (endPoint, data, dataInUrl = false) {
    return yield request("PUT", endPoint, data, dataInUrl);
  });

  const deleteRequest = _asyncToGenerator(function* (endPoint, data, dataInUrl = false) {
    return yield request("DELETE", endPoint, data, dataInUrl);
  });

  me.getUser = _asyncToGenerator(function*() {
    return yield getRequest(me.endPoints.userOperations.user);
  });

  me.getInitialDate = _asyncToGenerator(function*() {
    return yield getRequest(me.endPoints.timeEntryOperations.initialDate);
  });

  me.getTotalHours = _asyncToGenerator(function*() {
    return yield getRequest(me.endPoints.timeEntryOperations.totalHours);
  });

  me.getUserFavorites = _asyncToGenerator(function* () {
    return yield getRequest(me.endPoints.userOperations.userFavorites);
  });

  me.postProductGroupUserFavorite = _asyncToGenerator(function* (id) {
    return yield postRequest(
      me.endPoints.userOperations.userFavorites,
      {type: "productGroups", favoriteId: id},
      true
    );
  });

  me.postAccountUserFavorite = _asyncToGenerator(function* (id) {
    return yield postRequest(
      me.endPoints.userOperations.userFavorites,
      {type: "accounts", favoriteId: id},
      true
    );
  });

  me.postOpportunityUserFavorite = _asyncToGenerator(function* (id) {
    return yield postRequest(
      me.endPoints.userOperations.userFavorites,
      {type: "opportunities", favoriteId: id},
      true
    );
  });

  me.deleteProductGroupUserFavorite = _asyncToGenerator(function* (id) {
    return yield deleteRequest(
      me.endPoints.userOperations.userFavorites,
      {type: "productGroups", favoriteId: id},
      true
    );
  });

  me.deleteAccountUserFavorite = _asyncToGenerator(function* (id) {
    return yield deleteRequest(
      me.endPoints.userOperations.userFavorites,
      {type: "accounts", favoriteId: id},
      true
    );
  });

  me.deleteOpportunityUserFavorite = _asyncToGenerator(function* (id) {
    return yield deleteRequest(
      me.endPoints.userOperations.userFavorites,
      {type: "opportunities", favoriteId: id},
      true
    );
  });

  me.getUserPreferences = _asyncToGenerator(function* () {
    return yield getRequest(me.endPoints.userOperations.userPreferences);
  });

  me.saveUserPreferences = _asyncToGenerator(function* (userPreferencesObject) {
    try {
      return yield postRequest(
        me.endPoints.userOperations.userPreferences,
        userPreferencesObject
      );
    } catch(error) {}
  });

  me.getRoles = _asyncToGenerator(function* () {
    return yield getRequest(me.endPoints.userRoleOperations.roles);
  });

  me.editRoles = _asyncToGenerator(function* (id, notificationDelay, defaultWorkingHours) {
    let url = me.endPoints.userRoleOperations.rolesOps.replace("{id}", id);
    let roleObj = {notificationDelay, defaultWorkingHours};
    return yield putRequest(url, roleObj);
  });

  me.getCategories = _asyncToGenerator(function* (userRole) {
    return yield getRequest(
      me.endPoints.adminOperations.categories,
      {role: userRole}
    );
  });

  me.postCategories = _asyncToGenerator(function* (
    name = "",
    desc = "",
    roles = [],
    fields = []
  ) {
    return yield postRequest(
      me.endPoints.adminOperations.categories,
      {name, desc, roles, fields}
    );
  });

  me.editCategories = _asyncToGenerator(function* (
    id,
    name = "",
    desc = "",
    roles = [],
    fields = []
  ) {
    const url = me.endPoints.adminOperations.singleCategory.replace("{id}", id);
    const categoryObj = { name, desc, roles, fields };
    return yield putRequest(url, categoryObj);
  });

  me.deleteCategories = _asyncToGenerator(function* (id) {
    const url = me.endPoints.adminOperations.singleCategory.replace("{id}", id);

    return yield deleteRequest(url);
  });

  me.getAccounts = _asyncToGenerator(function* (data = {}) {
    const params = Object.assign({}, DEFAULT_API_ARGS, data);

    return yield getRequest(me.endPoints.accountOperations.accounts, params);
  });

  me.getProductGroups = _asyncToGenerator(function* (data = {}) {
    const params = Object.assign({}, DEFAULT_API_ARGS, data);

    return yield getRequest(me.endPoints.productOperations.productGroups, params);
  });

  me.getOpportunities = _asyncToGenerator(function* (data = {}) {
    const params = Object.assign({}, DEFAULT_API_ARGS, data);

    return yield getRequest(
      me.endPoints.opportunityOperations.opportunities,
      params
    );
  });

  me.getInterlocks = _asyncToGenerator(function* (data = {}) {
    const params = Object.assign({}, DEFAULT_API_ARGS, data);

    return yield getRequest(
      me.endPoints.interlockOperations.interlocks,
      params
    );
  });

  me.getDistributors = _asyncToGenerator(function* (data = {}) {
    const params = Object.assign({}, DEFAULT_API_ARGS, data);

    return yield getRequest(
      me.endPoints.distributorOperations.distributors,
      params
    );
  });

  me.getTimeEntries = _asyncToGenerator(function* (data = {}) {
    const params = Object.assign({size: 50, from: 0}, data);
    return yield getRequest(me.endPoints.timeEntryOperations.timeEntries, params);
  });

  me.getSingleTimeEntry = _asyncToGenerator(function* (id) {
    const url = me.endPoints.timeEntryOperations.timeEntriesOps.replace("{id}",id);
    return yield getRequest(url)
  });

  me.validateTimeEntriesData = ({
    category,
    categoryId,
    entryDate,
    hours,
    userId,
    accountId,
    locationId,
    productGroupId,
    productLineId,
    distributorId,
    opportunityId,
    comment,
    onsite,
    onsiteDetail,
    competitiveReplacement,
    serviceRequestId,
    interlockId
  }) => {
    hours = toFinite(hours);

    if (typeof category !== "string" || category === "") {
      throw "'category' parameter required";
    }

    if (typeof categoryId !== "string" || categoryId === "") {
      throw "'categoryId' parameter required";
    }

    if (typeof entryDate !== "string" || entryDate === "") {
      throw "'entryDate' parameter required";
    }

    if (hours <= 0) {
      throw "'hours' parameter required";
    }

    if (typeof userId !== "string" || userId === "") {
      throw "'userId' parameter required";
    }

    const data = { categoryId, category, entryDate, hours, userId };

    data.competitiveReplacement = Boolean(competitiveReplacement);

    data.onsite = Boolean(onsite);

    if (onsiteDetail) {
      data.onsiteDetail = onsiteDetail;
    } else {
      data.onsiteDetail = "not_with_customer";
    }

    if (accountId) {
      data.accountId = accountId;
    }

    if (locationId) {
      data.locationId = locationId;
    }

    if (productGroupId) {
      data.productGroupId = productGroupId;
    }

    if (productLineId) {
      data.productLineId = productLineId;
    }

    if (distributorId) {
      data.distributorId = distributorId;
    }

    if (opportunityId) {
      data.opportunityId = opportunityId;
    }

    if (comment) {
      data.comment = comment;
    }

    if (serviceRequestId) {
      data.serviceRequestId = serviceRequestId;
    }

    if (interlockId) {
      data.interlockId = interlockId;
    }

    return data;
  };

  me.postTimeEntries = _asyncToGenerator(function* (rawData = {}) {
    const data = me.validateTimeEntriesData(rawData);

    return yield postRequest(me.endPoints.timeEntryOperations.timeEntries, data);
  });

  me.editTimeEntries = _asyncToGenerator(function* (id, rawData = {}) {
    const data = me.validateTimeEntriesData(rawData);

    const url = me.endPoints.timeEntryOperations.timeEntriesOps.replace("{id}", id);

    return yield putRequest(url, data);
  });

  me.postTimeEntryBatch = _asyncToGenerator(function* (timeEntries, submit) {
    const data = {
      timeEntries: timeEntries.map(me.validateTimeEntriesData),
      submit: submit
    };

    return yield postRequest(me.endPoints.timeEntryOperations.timeEntriesBatch, data);
  });

  me.submitEntries = _asyncToGenerator(function* (id) {
    if (typeof id !== "string" || id == "") {
      throw "'id' parameter required";
    }

    const url = me.endPoints.timeEntryOperations.timeEntriesSubmit.replace("{id}", id);

    return yield postRequest(url);
  });

  me.deleteTimeEntries = _asyncToGenerator(function* (id) {
    try {
      if (typeof id !== "string" || id == "") {
        throw "'id' parameter required";
      }
      var url = me.endPoints.timeEntryOperations.timeEntriesOps.replace("{id}", id);
      return yield deleteRequest(url, {}, true);
    } catch (error) {
      return false;
    }
  });

  me.reportError = _asyncToGenerator(function* (environment, description, error) {
    const payload = {environment, description};

    if (error && error.message && error.stack) {
      payload.error = {
        message: error.message,
        stack: error.stack
      };
    };

    return yield putRequest(
      me.endPoints.errorReportOperations.reportError,
      payload
    );
  });

  me.getServiceRequests = _asyncToGenerator(function* (data = {}) {
    const params = Object.assign({}, DEFAULT_API_ARGS, data);

    return yield getRequest(
      me.endPoints.serviceRequestOperations.serviceRequests,
      params
    );
  });

  me.getSingleServiceRequest = _asyncToGenerator(function* (id) {
    const url = me.endPoints.serviceRequestOperations.singleServiceRequest.replace("{id}", id);

    return yield getRequest(url)
  });

  return me;
};

module.exports = MentorTimeTrackerApi;



// WEBPACK FOOTER //
// ./app/lib/MentorTimeTrackerApi.js